import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

type VerifyCustomerPayload = DefaultPayload & {
    mdn: string;
    pin: string;
};

export type ActivationFlowType = "REPLACEMENT_DEVICE" | "NEW_DEVICE";
export interface ClaimOrderSummaryForFrontend {
    claimId: string;
    deviceDescription: string;
    deviceSku?: string;
    shippedDate: string | null;
    insuranceType: string;
    ctn: string;
    secondaryCtn: string | null;
    isRequestedMdnMatch: boolean;
    requiresExtraAuth: boolean;
}

export interface VerifyCustomerResult {
    accountMdns: string[];
    activationFlowType: ActivationFlowType;
    claimSummaries: ClaimOrderSummaryForFrontend[];
    token: string;
    errorCode?: string;
    errorMessage?: string;
}

export const verifyCustomer = async (
    ssoAccessToken: string,
    payload: VerifyCustomerPayload,
    logger: Logger,
    apiBaseUrl: string,
) => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verify-customer`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        const { accountMdns, activationFlowType, claimSummaries, token } = await resp.json<VerifyCustomerResult>();
        return { accountMdns, activationFlowType, claimSummaries, token };
    } catch (error) {
        logger.warn({ error }, "Solve Guide | Error: Could not verify customer.");

        let errorCode = "";
        let errorMessage = "";

        // ky 2 consumes the error body into `error.data` before throwing; `error.response.json()`
        // would always reject (ew#7426).
        if (error instanceof HTTPError) {
            if (typeof error.data === "object" && error.data !== null) {
                const errorJson = error.data as { code?: string; message?: string };
                errorCode = errorJson.code || "";
                errorMessage = errorJson.message || "";

                logger.info({ errorCode, errorMessage }, "Solve Guide | API returned error message");
            } else {
                logger.warn(
                    { httpStatus: error.response.status },
                    "Solve Guide | verify-customer error body was not a JSON object",
                );
            }
        }

        return {
            accountMdns: [],
            activationFlowType: undefined,
            claimSummaries: [],
            token: undefined,
            errorCode,
            errorMessage,
        };
    }
};
